// Extensions Service - Firebase Firestore with localStorage fallback
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  getExtensions as getFirestoreExtensions,
  addExtension as addFirestoreExtension,
  updateExtension as updateFirestoreExtension,
  deleteExtension as deleteFirestoreExtension,
  approveExtension as approveFirestoreExtension,
  rejectExtension as rejectFirestoreExtension,
  incrementExtensionDownloads,
  onExtensionsChange,
  type FirestoreExtension,
} from './firebase/adminFirestore';
import { isFirebaseConfigured } from './firebase/config';

export type ExtensionStatus = 'approved' | 'pending' | 'rejected';
export type ExtensionCategory = 'Languages' | 'Themes' | 'SCM' | 'AI' | 'DevOps' | 'Database' | 'Snippets' | 'Debuggers' | 'Formatters' | 'Other';

export interface Extension {
  id: string;
  name: string;
  publisher: string;
  publisherEmail?: string;
  version: string;
  description: string;
  longDescription?: string;
  status: ExtensionStatus;
  downloads: number;
  rating: number;
  ratingCount: number;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  category: ExtensionCategory;
  tags: string[];
  icon?: string;
  repositoryUrl?: string;
  homepage?: string;
  license?: string;
  changelog?: string;
}

interface ExtensionsState {
  extensions: Extension[];
  isLoading: boolean;
  isFirebase: boolean;
  unsubscribe: (() => void) | null;

  // Actions
  initializeFromFirebase: () => Promise<void>;
  subscribeToFirebase: () => void;
  addExtension: (ext: Omit<Extension, 'id' | 'submittedAt' | 'downloads' | 'rating' | 'ratingCount'>) => Promise<Extension>;
  updateExtension: (id: string, updates: Partial<Extension>) => Promise<Extension | null>;
  deleteExtension: (id: string) => Promise<boolean>;
  getExtension: (id: string) => Extension | undefined;
  approveExtension: (id: string, reviewedBy: string) => Promise<boolean>;
  rejectExtension: (id: string, reviewedBy: string) => Promise<boolean>;
  incrementDownloads: (id: string) => Promise<void>;
  addRating: (id: string, rating: number) => void;
  getStats: () => ExtensionStats;
  getPendingExtensions: () => Extension[];
  getExtensionsByCategory: (category: ExtensionCategory) => Extension[];
}

export interface ExtensionStats {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
  totalDownloads: number;
  byCategory: Record<ExtensionCategory, number>;
  topDownloaded: Extension[];
  topRated: Extension[];
}

// Default extensions
const defaultExtensions: Extension[] = [
  {
    id: 'ext-1',
    name: 'Python Language Support',
    publisher: 'Ochtopus',
    version: '2.1.0',
    description: 'Rich support for the Python language, including IntelliSense, linting, debugging, and more.',
    status: 'approved',
    downloads: 125000,
    rating: 4.8,
    ratingCount: 2450,
    submittedAt: '2024-06-15T10:00:00Z',
    reviewedAt: '2024-06-16T14:30:00Z',
    category: 'Languages',
    tags: ['python', 'language', 'intellisense'],
  },
  {
    id: 'ext-2',
    name: 'Dark Pro Theme',
    publisher: 'ThemeStudio',
    version: '1.5.2',
    description: 'A professional dark theme with carefully crafted colors for reduced eye strain.',
    status: 'approved',
    downloads: 89000,
    rating: 4.6,
    ratingCount: 1820,
    submittedAt: '2024-07-20T08:00:00Z',
    reviewedAt: '2024-07-21T11:00:00Z',
    category: 'Themes',
    tags: ['theme', 'dark', 'professional'],
  },
  {
    id: 'ext-3',
    name: 'Git History',
    publisher: 'GitTools',
    version: '3.0.1',
    description: 'View git log, file history, compare branches or commits.',
    status: 'approved',
    downloads: 67000,
    rating: 4.5,
    ratingCount: 1340,
    submittedAt: '2024-08-10T12:00:00Z',
    reviewedAt: '2024-08-11T09:00:00Z',
    category: 'SCM',
    tags: ['git', 'history', 'version-control'],
  },
];

// Convert Firestore extension to local format
function firestoreToLocal(ext: FirestoreExtension): Extension {
  return {
    id: ext.id,
    name: ext.name,
    publisher: ext.publisher,
    publisherEmail: ext.publisherEmail,
    version: ext.version,
    description: ext.description,
    longDescription: ext.longDescription,
    status: ext.status,
    downloads: ext.downloads,
    rating: ext.rating,
    ratingCount: ext.ratingCount,
    submittedAt: ext.submittedAt.toISOString(),
    reviewedAt: ext.reviewedAt?.toISOString(),
    reviewedBy: ext.reviewedBy,
    category: ext.category as ExtensionCategory,
    tags: ext.tags,
    icon: ext.icon,
    repositoryUrl: ext.repositoryUrl,
  };
}

export const useExtensionsStore = create<ExtensionsState>()(
  persist(
    (set, get) => ({
      extensions: defaultExtensions,
      isLoading: false,
      isFirebase: false,
      unsubscribe: null,

      initializeFromFirebase: async () => {
        if (!isFirebaseConfigured()) {
          set({ isFirebase: false });
          return;
        }

        set({ isLoading: true });
        try {
          const firestoreExts = await getFirestoreExtensions();
          if (firestoreExts.length > 0) {
            const extensions = firestoreExts.map(firestoreToLocal);
            set({ extensions, isFirebase: true, isLoading: false });
          } else {
            set({ isFirebase: true, isLoading: false });
          }
        } catch {
          set({ isFirebase: false, isLoading: false });
        }
      },

      subscribeToFirebase: () => {
        if (!isFirebaseConfigured()) return;

        const unsubscribe = onExtensionsChange((firestoreExts) => {
          if (firestoreExts.length > 0) {
            const extensions = firestoreExts.map(firestoreToLocal);
            set({ extensions, isFirebase: true });
          }
        });

        set({ unsubscribe });
      },

      addExtension: async (extData) => {
        const newExtension: Extension = {
          ...extData,
          id: `ext-${Date.now()}`,
          submittedAt: new Date().toISOString(),
          downloads: 0,
          rating: 0,
          ratingCount: 0,
        };

        if (isFirebaseConfigured()) {
          try {
            const id = await addFirestoreExtension({
              name: extData.name,
              publisher: extData.publisher,
              publisherEmail: extData.publisherEmail,
              version: extData.version,
              description: extData.description,
              longDescription: extData.longDescription,
              status: extData.status,
              category: extData.category,
              tags: extData.tags,
              icon: extData.icon,
              repositoryUrl: extData.repositoryUrl,
            });
            newExtension.id = id;
          } catch {
            // Silent fail
          }
        }

        set((state) => ({
          extensions: [...state.extensions, newExtension],
        }));

        return newExtension;
      },

      updateExtension: async (id, updates) => {
        const state = get();
        const index = state.extensions.findIndex((e) => e.id === id);
        if (index === -1) return null;

        const updatedExtension = { ...state.extensions[index], ...updates };
        const newExtensions = [...state.extensions];
        newExtensions[index] = updatedExtension;

        if (isFirebaseConfigured()) {
          try {
            await updateFirestoreExtension(id, updates as unknown as Partial<FirestoreExtension>);
          } catch {
            // Silent fail
          }
        }

        set({ extensions: newExtensions });
        return updatedExtension;
      },

      deleteExtension: async (id) => {
        if (isFirebaseConfigured()) {
          try {
            await deleteFirestoreExtension(id);
          } catch {
            // Silent fail
          }
        }

        set((state) => ({
          extensions: state.extensions.filter((e) => e.id !== id),
        }));
        return true;
      },

      getExtension: (id) => {
        return get().extensions.find((e) => e.id === id);
      },

      approveExtension: async (id, reviewedBy) => {
        if (isFirebaseConfigured()) {
          try {
            await approveFirestoreExtension(id, reviewedBy);
          } catch {
            // Silent fail
          }
        }

        const result = await get().updateExtension(id, {
          status: 'approved',
          reviewedAt: new Date().toISOString(),
          reviewedBy,
        });
        return result !== null;
      },

      rejectExtension: async (id, reviewedBy) => {
        if (isFirebaseConfigured()) {
          try {
            await rejectFirestoreExtension(id, reviewedBy);
          } catch {
            // Silent fail
          }
        }

        const result = await get().updateExtension(id, {
          status: 'rejected',
          reviewedAt: new Date().toISOString(),
          reviewedBy,
        });
        return result !== null;
      },

      incrementDownloads: async (id) => {
        const ext = get().getExtension(id);
        if (ext) {
          if (isFirebaseConfigured()) {
            try {
              await incrementExtensionDownloads(id);
            } catch {
              // Silent fail
            }
          }
          await get().updateExtension(id, { downloads: ext.downloads + 1 });
        }
      },

      addRating: (id, rating) => {
        const ext = get().getExtension(id);
        if (ext) {
          const newCount = ext.ratingCount + 1;
          const newRating = ((ext.rating * ext.ratingCount) + rating) / newCount;
          get().updateExtension(id, {
            rating: Math.round(newRating * 10) / 10,
            ratingCount: newCount,
          });
        }
      },

      getPendingExtensions: () => {
        return get().extensions.filter((e) => e.status === 'pending');
      },

      getExtensionsByCategory: (category) => {
        return get().extensions.filter((e) => e.category === category && e.status === 'approved');
      },

      getStats: () => {
        const extensions = get().extensions;
        const approved = extensions.filter((e) => e.status === 'approved');

        const categories: ExtensionCategory[] = ['Languages', 'Themes', 'SCM', 'AI', 'DevOps', 'Database', 'Snippets', 'Debuggers', 'Formatters', 'Other'];
        const byCategory = {} as Record<ExtensionCategory, number>;
        categories.forEach((cat) => {
          byCategory[cat] = extensions.filter((e) => e.category === cat).length;
        });

        const stats: ExtensionStats = {
          total: extensions.length,
          approved: extensions.filter((e) => e.status === 'approved').length,
          pending: extensions.filter((e) => e.status === 'pending').length,
          rejected: extensions.filter((e) => e.status === 'rejected').length,
          totalDownloads: extensions.reduce((sum, e) => sum + e.downloads, 0),
          byCategory,
          topDownloaded: [...approved].sort((a, b) => b.downloads - a.downloads).slice(0, 5),
          topRated: [...approved].sort((a, b) => b.rating - a.rating).slice(0, 5),
        };

        return stats;
      },
    }),
    {
      name: 'ochtopus-extensions',
      partialize: (state) => ({
        extensions: state.extensions,
      }),
    }
  )
);

// Initialize Firebase on import
if (typeof window !== 'undefined') {
  useExtensionsStore.getState().initializeFromFirebase();
  useExtensionsStore.getState().subscribeToFirebase();
}
